import React, { useState, useEffect, useMemo } from 'react';
import { Shield, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Eye } from 'lucide-react';
import { getApiUrl } from '@/config/api';
import { authenticatedFetchWithRetry } from '@/utils/apiUtils';
import { SparklineChart, InfoTooltip } from '@/components/shared';
import { INJURY_RISK_TOOLTIP } from '@/constants/tooltips';
import { InjuryRiskHistoryPoint } from '@/types';

interface TeamInjuryRiskPanelProps {
  athletes: Array<{ id: number; name: string }>;
  isDark: boolean;
  onAthleteClick: (athleteId: number) => void;
}

interface SignalContribution {
  signal: string;
  raw_value: number;
  risk_score: number;
  weighted_contribution: number;
  direction: string;
  explanation: string;
}

interface InjuryRiskResponse {
  risk_score: number;
  tier: string;
  confidence: number;
  signal_contributions?: SignalContribution[];
  coach_actions: string[];
  interaction_penalties: string[];
  data_quality: {
    signals_present: number;
    signals_total: number;
    shap_coverage: number;
    subjective_available: boolean;
    confidence_note: string;
  };
  date: string;
  status?: string;
}

const TIER_CONFIG: Record<string, { label: string; color: string }> = {
  LOW: { label: 'Low', color: '#22c55e' },
  MODERATE: { label: 'Moderate', color: '#eab308' },
  ELEVATED: { label: 'Elevated', color: '#f97316' },
  HIGH: { label: 'High', color: '#ef4444' },
  CRITICAL: { label: 'Critical', color: '#991b1b' },
};

const TIER_ORDER = ['CRITICAL', 'HIGH', 'ELEVATED', 'MODERATE', 'LOW'];
const ELEVATED_TIERS = new Set(['ELEVATED', 'HIGH', 'CRITICAL']);

interface AthleteRisk {
  id: number;
  name: string;
  data: InjuryRiskResponse | null;
  history: InjuryRiskHistoryPoint[] | null;
  error: boolean;
}

const TeamInjuryRiskPanel: React.FC<TeamInjuryRiskPanelProps> = ({ athletes, isDark, onAthleteClick }) => {
  const [riskMap, setRiskMap] = useState<Map<number, InjuryRiskResponse | null>>(new Map());
  const [historyMap, setHistoryMap] = useState<Map<number, InjuryRiskHistoryPoint[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [errorIds, setErrorIds] = useState<Set<number>>(new Set());
  const [isExpanded, setIsExpanded] = useState(true);

  // Fetch risk scores for all athletes
  useEffect(() => {
    if (!athletes || athletes.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      const results = new Map<number, InjuryRiskResponse | null>();
      const errors = new Set<number>();

      await Promise.all(
        athletes.map(async (athlete) => {
          try {
            const url = getApiUrl(`/api/injury-risk/${athlete.id}/?days=45&include_details=true`);
            const result = await authenticatedFetchWithRetry<InjuryRiskResponse>(
              url, { method: 'GET' }, 'fetching injury risk data', 1
            );
            if (result.success && result.data) {
              const data = result.data;
              if (data.status === 'no_data' || data.status === 'insufficient_data') {
                results.set(athlete.id, null);
              } else {
                results.set(athlete.id, data);
              }
            } else {
              results.set(athlete.id, null);
              errors.add(athlete.id);
            }
          } catch {
            results.set(athlete.id, null);
            errors.add(athlete.id);
          }
        })
      );

      if (!cancelled) {
        setRiskMap(results);
        setErrorIds(errors);
        setLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [athletes]);

  // Fetch history for elevated+ athletes (after risk scores load)
  useEffect(() => {
    if (loading || riskMap.size === 0) return;

    let cancelled = false;
    const elevatedIds: number[] = [];
    for (const [id, data] of riskMap) {
      if (data && ELEVATED_TIERS.has(data.tier)) {
        elevatedIds.push(id);
      }
    }

    if (elevatedIds.length === 0) return;

    const fetchHistory = async () => {
      const results = new Map<number, InjuryRiskHistoryPoint[]>();

      await Promise.all(
        elevatedIds.map(async (id) => {
          try {
            const url = getApiUrl(`/api/injury-risk/${id}/history/?days_history=14`);
            const result = await authenticatedFetchWithRetry<{ history: InjuryRiskHistoryPoint[] }>(
              url, { method: 'GET' }, 'fetching injury risk history', 1
            );
            if (result.success && result.data?.history) {
              results.set(id, result.data.history);
            }
          } catch {
            // History fetch failure is non-critical
          }
        })
      );

      if (!cancelled) {
        setHistoryMap(results);
      }
    };

    fetchHistory();
    return () => { cancelled = true; };
  }, [loading, riskMap]);

  // Build sorted athlete list
  const sortedAthletes: AthleteRisk[] = useMemo(() => {
    const withData: AthleteRisk[] = [];
    const withoutData: AthleteRisk[] = [];

    for (const athlete of athletes) {
      const data = riskMap.get(athlete.id) ?? null;
      const history = historyMap.get(athlete.id) ?? null;
      const entry = { id: athlete.id, name: athlete.name, data, history, error: errorIds.has(athlete.id) };
      if (data) {
        withData.push(entry);
      } else {
        withoutData.push(entry);
      }
    }

    withData.sort((a, b) => (b.data?.risk_score ?? 0) - (a.data?.risk_score ?? 0));
    return [...withData, ...withoutData];
  }, [athletes, riskMap, errorIds, historyMap]);

  const atRiskAthletes = useMemo(
    () => sortedAthletes.filter(a => a.data && ELEVATED_TIERS.has(a.data.tier)),
    [sortedAthletes]
  );

  // Tier distribution counts
  const tierCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [, data] of riskMap) {
      if (data?.tier) {
        counts[data.tier] = (counts[data.tier] || 0) + 1;
      }
    }
    return counts;
  }, [riskMap]);

  if (loading) {
    return (
      <div className="cgm-metric-card rounded-2xl p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="w-8 h-8 cgm-text-secondary" />
          <h3 className="text-lg font-bold cgm-text-primary">Team Injury Risk</h3>
        </div>
        <div className="flex items-center justify-center h-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3"></div>
            <p className="cgm-text-secondary text-sm">Calculating team injury risk...</p>
          </div>
        </div>
      </div>
    );
  }

  if (sortedAthletes.length === 0) return null;
  const hasAnyData = sortedAthletes.some(a => a.data !== null);

  return (
    <div className={`cgm-metric-card rounded-2xl p-6 border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <Shield className="w-8 h-8" style={{ color: '#ef4444' }} />
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-lg font-bold cgm-text-primary">Team Injury Risk</h3>
              <InfoTooltip
                title={INJURY_RISK_TOOLTIP.title}
                body={INJURY_RISK_TOOLTIP.body}
                isDark={isDark}
                iconSize={3.5}
                ariaLabel="About injury risk scoring"
              />
            </div>
            {hasAnyData && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {TIER_ORDER.map((tier) => {
                  const count = tierCounts[tier];
                  if (!count) return null;
                  const config = TIER_CONFIG[tier];
                  return (
                    <span
                      key={tier}
                      className="text-xs font-semibold px-2 py-0.5 rounded"
                      style={{ backgroundColor: `${config.color}20`, color: config.color }}
                    >
                      {count} {config.label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <button className="text-gray-400 hover:text-gray-200 transition-colors p-1">
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {/* Content */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[4000px] opacity-100 overflow-visible mt-6' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        {!hasAnyData ? (
          <div className="text-center py-8">
            <p className="cgm-text-secondary text-sm">
              No injury risk data available yet. Athletes need wearable data to generate risk scores.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* At-Risk Athletes Table */}
            {atRiskAthletes.length > 0 && (
              <AtRiskAthletesTable
                athletes={atRiskAthletes}
                isDark={isDark}
                onAthleteClick={onAthleteClick}
              />
            )}

            {/* Team Risk Heatmap */}
            {/* <TeamRiskHeatmap
              athletes={sortedAthletes.filter(a => a.data !== null)}
              isDark={isDark}
              onAthleteClick={onAthleteClick}
            /> */}
          </div>
        )}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// At-Risk Athletes Table
// ---------------------------------------------------------------------------

interface AtRiskTableProps {
  athletes: AthleteRisk[];
  isDark: boolean;
  onAthleteClick: (id: number) => void;
}

const AtRiskAthletesTable: React.FC<AtRiskTableProps> = ({ athletes, isDark, onAthleteClick }) => {
  return (
    <div>
      <h4 className="text-sm font-semibold cgm-text-primary mb-3">
        At-Risk Athletes
        <span className="text-xs font-normal cgm-text-secondary ml-2">
          ({athletes.length} in ELEVATED+ tiers)
        </span>
      </h4>

      {/* Table header */}
      <div className={`grid grid-cols-[1fr_100px_100px_1fr_80px_60px] gap-3 px-3 pb-2 text-xs font-medium cgm-text-secondary border-b ${isDark ? 'border-neutral-700' : 'border-gray-200'}`}>
        <span>Athlete</span>
        <span>Risk Score</span>
        <span>Trend (14d)</span>
        <span>Top Factor</span>
        <span className="text-center">Days Elevated</span>
        <span></span>
      </div>

      {/* Table rows */}
      <div className="divide-y divide-gray-200/30 dark:divide-neutral-700/30">
        {athletes.map((athlete) => {
          const data = athlete.data!;
          const config = TIER_CONFIG[data.tier] || TIER_CONFIG.LOW;
          const riskPercent = Math.round(data.risk_score * 100);
          const initials = getInitials(athlete.name);

          // Sparkline data from history
          const sparklineData = athlete.history?.map(h => h.risk_score * 100) || [];

          // Days elevated: count consecutive days from end of history in ELEVATED+ tiers
          const daysElevated = countDaysElevated(athlete.history);

          // Top factor from signal contributions
          const topContrib = data.signal_contributions
            ?.filter(c => c.direction === 'increasing_risk')
            .sort((a, b) => b.weighted_contribution - a.weighted_contribution)[0];

          // Trend direction (compare first half avg to second half avg)
          const trend = computeTrend(sparklineData);

          return (
            <div
              key={athlete.id}
              className={`grid grid-cols-[1fr_100px_100px_1fr_80px_60px] gap-3 items-center px-3 py-3 hover:scale-[1.005] transition-all duration-200 cursor-pointer ${isDark ? 'hover:bg-neutral-800/50' : 'hover:bg-gray-50'}`}
              onClick={() => onAthleteClick(athlete.id)}
            >
              {/* Athlete */}
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: config.color }}
                >
                  {initials}
                </div>
                <span className="text-sm font-semibold cgm-text-primary truncate">{athlete.name}</span>
              </div>

              {/* Risk Score */}
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold" style={{ color: config.color }}>
                  {riskPercent}
                </span>
                <span
                  className="text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: config.color }}
                >
                  {config.label}
                </span>
              </div>

              {/* Trend sparkline */}
              <div className="flex items-center gap-1">
                {sparklineData.length > 2 ? (
                  <>
                    <SparklineChart data={sparklineData} color={config.color} width={80} height={28} />
                    {trend !== 0 && (
                      trend > 0
                        ? <TrendingUp className="w-3 h-3 text-red-500" />
                        : <TrendingDown className="w-3 h-3 text-green-500" />
                    )}
                  </>
                ) : (
                  <span className="text-xs cgm-text-secondary">-</span>
                )}
              </div>

              {/* Top Factor */}
              <div className="min-w-0">
                {topContrib ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs cgm-text-secondary">&#8593;</span>
                    <span className="text-xs font-medium cgm-text-primary truncate">
                      {formatTopFactor(topContrib)}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs cgm-text-secondary">-</span>
                )}
              </div>

              {/* Days Elevated */}
              <div className="text-center">
                <span className="text-sm font-semibold cgm-text-primary">{daysElevated}</span>
              </div>

              {/* View button */}
              <button
                className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'bg-neutral-700 hover:bg-neutral-600 text-neutral-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                onClick={(e) => { e.stopPropagation(); onAthleteClick(athlete.id); }}
              >
                View
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Team Risk Heatmap
// ---------------------------------------------------------------------------

interface HeatmapProps {
  athletes: AthleteRisk[];
  isDark: boolean;
  onAthleteClick: (id: number) => void;
}

const TeamRiskHeatmap: React.FC<HeatmapProps> = ({ athletes, isDark, onAthleteClick }) => {
  return (
    <div>
      <h4 className="text-sm font-semibold cgm-text-primary mb-3">Team Risk Heatmap</h4>
      <div className="grid grid-cols-5 gap-3">
        {athletes.map((athlete) => {
          const data = athlete.data!;
          const config = TIER_CONFIG[data.tier] || TIER_CONFIG.LOW;
          const riskPercent = Math.round(data.risk_score * 100);
          const initials = getInitials(athlete.name);
          const firstName = athlete.name.split(' ')[0];

          return (
            <div
              key={athlete.id}
              className={`flex flex-col items-center p-3 rounded-xl border-b-[3px] cursor-pointer transition-all duration-200 hover:scale-105 ${isDark ? 'bg-neutral-800/40 border-neutral-700/30' : 'bg-gray-50 border-gray-200'}`}
              style={{ borderBottomColor: config.color }}
              onClick={() => onAthleteClick(athlete.id)}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white mb-1.5"
                style={{ backgroundColor: config.color }}
              >
                {initials}
              </div>
              <span className="text-xs font-medium cgm-text-primary truncate max-w-full">{firstName}</span>
              <span className="text-lg font-bold mt-0.5" style={{ color: config.color }}>
                {riskPercent}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function countDaysElevated(history: InjuryRiskHistoryPoint[] | null): number {
  if (!history || history.length === 0) return 0;
  let count = 0;
  for (let i = history.length - 1; i >= 0; i--) {
    if (ELEVATED_TIERS.has(history[i].tier)) {
      count++;
    } else {
      break;
    }
  }
  return count;
}

function computeTrend(data: number[]): number {
  if (data.length < 4) return 0;
  const mid = Math.floor(data.length / 2);
  const firstHalf = data.slice(0, mid);
  const secondHalf = data.slice(mid);
  const avg1 = firstHalf.reduce((s, v) => s + v, 0) / firstHalf.length;
  const avg2 = secondHalf.reduce((s, v) => s + v, 0) / secondHalf.length;
  const diff = avg2 - avg1;
  if (Math.abs(diff) < 2) return 0; // within noise band
  return diff > 0 ? 1 : -1;
}

const SIGNAL_SHORT_NAMES: Record<string, string> = {
  soreness_3d_max: 'Soreness (3d)',
  soreness: 'Soreness',
  mental_fatigue: 'Mental Fatigue',
  rpe: 'RPE',
  motivation: 'Motivation',
  sleep_hours: 'Sleep Deficit',
  sleep_score: 'Sleep Quality',
  rhr: 'RHR',
  hrv_rmssd: 'HRV Cliff',
  hrv_7d_cv: 'HRV Variability',
  hrv_slope: 'HRV Trend',
  rhr_slope: 'RHR Trend',
  acwr_composite: 'ACWR',
  session_load_spike_ratio: 'Load Spike',
  load_change_pct_7d: 'Load Change',
  load_7d_sum: '7d Load',
  consecutive_high_soreness: 'Persistent Soreness',
  rpe_load_divergence: 'RPE Mismatch',
  resp_rate: 'Resp Rate',
  skin_temp: 'Skin Temp',
  load_volatility: 'Load Volatility',
  wearable_missing_rate_7d: 'Data Gap',
};

function formatTopFactor(contrib: SignalContribution): string {
  const name = SIGNAL_SHORT_NAMES[contrib.signal] || contrib.signal;
  const raw = contrib.raw_value;

  // Format raw value contextually
  if (contrib.signal === 'sleep_hours' && raw > 0) return `${name} ${raw.toFixed(1)} hrs`;
  if (contrib.signal === 'hrv_rmssd') return `${name} ${raw > 0 ? raw.toFixed(0) : ''}ms`;
  if (contrib.signal === 'acwr_composite') return `${name} ${raw.toFixed(2)}`;
  if (['soreness', 'soreness_3d_max', 'rpe', 'motivation', 'mental_fatigue'].includes(contrib.signal)) {
    return `${name} ${raw.toFixed(0)}/10`;
  }
  if (contrib.signal.includes('pct') || contrib.signal.includes('change')) {
    return `${name} ${raw > 0 ? '+' : ''}${raw.toFixed(0)}%`;
  }

  return name;
}

export default TeamInjuryRiskPanel;
